/* cc-theme.js: de bewegingen uit de app, in gewone JS (geen build-step).
   1. CTA-wiebel: bij hover knijpt elke letter samen, draait, flitst in een pastelkleur en veert terug
      (met stagger), de knop zelf veert iets uit; bij klik een squash-and-stretch. Zelfde timing als
      CtaButton in de app (src/components/ploy/cta-button.tsx). Alleen met muis, niet op touch.
   2. Scroll-vulling van sectiekoppen: elk woord vult zich van links met inkt terwijl de kop door het
      beeld scrolt (zoals DisplayHeading met scrollFill in de app). CSS: .cc-word / .cc-word-fill.
   3. Zwevende navigatie: zet .cc-nav--scrolled op .cc-nav zodra de pagina 24px gescrold is (logo veert weg, CSS).
   4. Uitleg bij inloggen met de nieuwe klantaccounts: kaart #cc-login voor [data-cc-login]-links (zie blok 4).
   Respecteert prefers-reduced-motion (1 en 2 staan dan uit; 3 werkt altijd). Geladen met defer vanuit layout/theme.liquid. */
(function () {
  "use strict";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var motion = !reduced && "animate" in Element.prototype;

  /* ---------- 1. CTA-wiebel ---------- */
  var CTA_SELECTOR = ".cc-cta, .cc-hero .slide__block-link, .bls__banner-text--button, .bls__banner-btn, .cc-footer .bls__form-submit";
  var SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";
  var rootStyle = getComputedStyle(document.documentElement);
  var colors = ["--cc-pink", "--cc-yellow", "--cc-blue", "--cc-green"].map(function (v) {
    return rootStyle.getPropertyValue(v).trim();
  }).filter(Boolean);
  var lastColor = null;

  function pickColor() {
    var pool = colors.filter(function (c) { return c !== lastColor; });
    lastColor = pool[Math.floor(Math.random() * pool.length)] || colors[0];
    return lastColor;
  }

  // Tekst per teken in spans; alleen tekstknopen direct in de knop of in een <span> zonder kind-elementen.
  function splitChars(el) {
    var targets = [];
    Array.prototype.forEach.call(el.childNodes, function (n) {
      if (n.nodeType === 3 && n.textContent.trim()) targets.push(n);
      else if (n.nodeType === 1 && n.tagName === "SPAN" && n.children.length === 0 && n.textContent.trim()) targets.push(n);
    });
    if (!targets.length) return [];
    var chars = [];
    targets.forEach(function (n) {
      var text = n.textContent.replace(/\s+/g, " ");
      var frag = document.createDocumentFragment();
      Array.prototype.forEach.call(text, function (ch) {
        var s = document.createElement("span");
        s.className = "cc-cta__char" + (ch === " " ? " cc-cta__char--space" : "");
        s.setAttribute("aria-hidden", "true");
        s.textContent = ch === " " ? " " : ch;
        frag.appendChild(s);
        chars.push(s);
      });
      if (n.nodeType === 3) {
        // losse tekst eerst in één span, anders wordt elke letter een flex-item (en krijgt hij de gap van de knop)
        var wrap = document.createElement("span");
        wrap.className = "cc-cta__text";
        wrap.appendChild(frag);
        n.parentNode.replaceChild(wrap, n);
      } else { n.textContent = ""; n.appendChild(frag); }
    });
    if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", text_of(targets));
    return chars;
  }
  function text_of(nodes) {
    return nodes.map(function (n) { return n.textContent; }).join("").replace(/\s+/g, " ").trim();
  }

  function initCta(el) {
    // het CTA-blok in blogartikelen (div.cc-cta in de artikeltekst, assets/cc-blog-article.css) is geen knop
    if (el.closest(".article-template__content")) return;
    if (el.dataset.ccCta) return;
    el.dataset.ccCta = "1";
    var chars = splitChars(el);
    if (!chars.length) return;
    el.classList.add("cc-cta--js");
    var isTouch = function (e) { return e && e.pointerType === "touch"; };

    var wiggle = function (e) {
      if (isTouch(e)) return;
      var color = pickColor();
      var base = getComputedStyle(el).color;
      chars.forEach(function (c, i) {
        c.animate(
          [
            { transform: "scaleY(1) rotate(0deg)", color: base, offset: 0 },
            { transform: "scaleY(0.25) rotate(20deg)", color: color, offset: 0.2, easing: SPRING },
            { transform: "scaleY(1) rotate(0deg)", color: base, offset: 1 },
          ],
          { duration: 700, delay: i * 17, easing: "ease-in", fill: "none" }
        );
      });
      el.animate([{ transform: "scale(1, 1)" }, { transform: "scale(1.05, 1.08)" }], { duration: 320, easing: SPRING, fill: "forwards" });
    };
    var rest = function (e) {
      if (isTouch(e)) return;
      el.animate([{ transform: "scale(1.05, 1.08)" }, { transform: "scale(1, 1)" }], { duration: 400, easing: "ease-out", fill: "forwards" });
    };
    var press = function () {
      el.animate(
        [
          { transform: "scale(1, 1)", offset: 0 },
          { transform: "scale(1.12, 1.15)", offset: 0.23 },
          { transform: "scale(0.95, 0.93)", offset: 0.46 },
          { transform: "scale(1.04, 1.05)", offset: 0.69 },
          { transform: "scale(1, 1)", offset: 1 },
        ],
        { duration: 650, easing: "ease-out", fill: "forwards" }
      );
    };
    el.addEventListener("pointerenter", wiggle);
    el.addEventListener("focus", wiggle);
    el.addEventListener("pointerleave", rest);
    el.addEventListener("blur", rest);
    el.addEventListener("click", press);
  }

  /* ---------- 2. Scroll-vulling van koppen ---------- */
  var HEADING_SELECTOR = ".bls__section-heading, .cc-vhb-titel, .bls__banner-text--heading";
  var headings = [];

  function initHeading(el) {
    if (el.dataset.ccFill || el.children.length || !el.textContent.trim()) return; // alleen platte tekst
    if (el.closest(".bls__super-deal-header")) return; // die kop is een intro-regel, geen display-kop
    el.dataset.ccFill = "1";
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = "";
    // Elk woord staat maar één keer als echte tekst in de pagina (Google en schermlezers lezen "Trending",
    // niet "TrendingTrending"); de gevulde laag is een ::after met content: attr(data-w), zie cc-theme.css.
    var spans = words.map(function (w, i) {
      var s = document.createElement("span");
      s.className = "cc-word";
      s.setAttribute("data-w", w);
      s.textContent = w;
      el.appendChild(s);
      if (i < words.length - 1) el.appendChild(document.createTextNode(" "));
      return s;
    });
    headings.push({ el: el, words: spans });
  }

  var ticking = false;
  function update() {
    ticking = false;
    var vh = window.innerHeight;
    headings.forEach(function (h) {
      var r = h.el.getBoundingClientRect();
      // zelfde venster als in de app: start als de kop op 88% van het beeld staat, klaar op 45%
      var start = vh * 0.88, end = vh * 0.45;
      var p = (start - r.top) / (start - end + r.height);
      p = Math.max(0, Math.min(1, p));
      var n = h.words.length;
      h.words.forEach(function (w, i) {
        var s = i / (n + 0.5), e = (i + 1.5) / (n + 0.5);
        var wp = Math.max(0, Math.min(1, (p - s) / (e - s)));
        w.style.setProperty("--p", (wp * 100).toFixed(1) + "%");
      });
    });
  }
  function onScroll() {
    if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
  }

  /* ---------- 3. Zwevende navigatie: logo veert weg zodra de pagina scrolt ---------- */
  var nav = document.querySelector(".cc-nav");
  var SCROLL_HIDE_AT = 24;
  function navScroll() {
    if (nav) nav.classList.toggle("cc-nav--scrolled", window.scrollY > SCROLL_HIDE_AT);
  }

  /* ---------- 4. Uitleg bij inloggen (nieuwe klantaccounts, snippets/cc-login-notice.liquid) ----------
     Klik op een inloglink [data-cc-login] opent eerst de kaart #cc-login; de knop daarin gaat naar Shopify's
     inlogpagina. Na die klik onthoudt dit apparaat dat de uitleg gezien is (localStorage cc_login_notice_seen)
     en gaan inloglinks daarna meteen door. Zonder kaart (klassieke accounts, ingelogd, uitleg uit) doet dit niets. */
  var LOGIN_SEEN_KEY = "cc_login_notice_seen";
  var loginBound = false;
  function loginSeen() {
    try { return window.localStorage.getItem(LOGIN_SEEN_KEY) === "1"; } catch (e) { return false; }
  }
  function initLoginNotice() {
    var box = document.getElementById("cc-login");
    if (!box || loginBound) return;
    loginBound = true;
    var lastTrigger = null;
    var closeTimer = null;
    function open(trigger) {
      lastTrigger = trigger || null;
      clearTimeout(closeTimer);
      box.hidden = false;
      document.documentElement.classList.add("cc-login-open");
      // eerst tonen, dan de klasse: anders speelt de overgang niet
      requestAnimationFrame(function () { requestAnimationFrame(function () { box.classList.add("is-open"); }); });
      var cta = box.querySelector("[data-cc-login-go]");
      if (cta) setTimeout(function () { cta.focus({ preventScroll: true }); }, 60);
    }
    function close() {
      box.classList.remove("is-open");
      document.documentElement.classList.remove("cc-login-open");
      closeTimer = setTimeout(function () { box.hidden = true; }, reduced ? 0 : 220);
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    }
    document.addEventListener("click", function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var trigger = e.target.closest("[data-cc-login]");
      if (trigger) {
        if (loginSeen()) return;
        e.preventDefault();
        e.stopPropagation(); // Umino's menu- en tooltip-handlers niet laten meedoen
        open(trigger);
        return;
      }
      if (box.hidden) return;
      if (e.target.closest("[data-cc-login-close]")) { e.preventDefault(); close(); return; }
      if (e.target.closest("[data-cc-login-go]")) {
        try { window.localStorage.setItem(LOGIN_SEEN_KEY, "1"); } catch (err) { /* privévenster: dan zie je de uitleg opnieuw */ }
      }
    }, true);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !box.hidden) close();
    });
  }

  function init() {
    if (motion) {
      document.querySelectorAll(CTA_SELECTOR).forEach(initCta);
      document.querySelectorAll(HEADING_SELECTOR).forEach(initHeading);
    }
    if (nav) { window.addEventListener("scroll", navScroll, { passive: true }); navScroll(); }
    initLoginNotice();
    if (headings.length) {
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      update();
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  // secties die de editor opnieuw rendert
  document.addEventListener("shopify:section:load", init);
})();
